/**
 * Build the gateway `send` payload from the host's optional `systemPreamble`,
 * the user's typed `content`, and optional per-message `messageContext`.
 *
 * Contract: the chat bubble/title always show the raw `content`; only the
 * outbound gateway payload carries the preamble and the message context. Both
 * extras are hidden the same way — appended to the wire payload, never to the
 * visible transcript.
 *
 * `messageContext` is per-call hidden context (e.g. the structured data for a
 * `/analyze`d trade) — distinct from `systemPreamble`, which is static and
 * rides on every turn. Ordering on the wire is preamble → content → context:
 * identity, then the user's ask, then the reference data for it. Blank /
 * whitespace-only extras are dropped so we never send `"   \n\n<msg>"`.
 */
export function buildSendPayload(
  systemPreamble: string | undefined,
  content: string,
  messageContext?: string,
  /**
   * The user's language choice, from Settings. Framing like the preamble, so
   * it rides every turn and cannot drift out of context on a long chat.
   *
   * Placed AFTER the host's preamble deliberately: if a host's framing also
   * says something about language, the user's explicit setting is the more
   * specific and more recent instruction and should be the one that lands.
   */
  languageDirective?: string | null,
): string {
  // A slash command must START the payload. The gateway dispatches a command
  // only at position 0, so PREPENDED framing turns `/help` into prose — every
  // composer-typed command (`/help`, `/model`, `/clear`, skill commands)
  // silently stopped working the moment a reply language was set. The
  // codebase already knows this rule: both programmatic `/model` seeds send
  // through `ws.send` directly, bypassing this builder. So the preamble and
  // the directive are skipped — but `messageContext` still rides, because it
  // APPENDS: trailing data doesn't displace position 0, and a command
  // carrying hidden data is `context`'s documented use (see `sendMessage`'s
  // `opts.context` — "the structured data for a `/analyze`d trade").
  if (content.trimStart().startsWith('/')) {
    return messageContext != null && messageContext.trim().length > 0
      ? [content, messageContext].join('\n\n')
      : content;
  }

  const parts: string[] = [];
  if (systemPreamble != null && systemPreamble.trim().length > 0) {
    parts.push(systemPreamble);
  }
  if (languageDirective != null && languageDirective.trim().length > 0) {
    parts.push(languageDirective);
  }
  parts.push(content);
  if (messageContext != null && messageContext.trim().length > 0) {
    parts.push(messageContext);
  }
  return parts.join('\n\n');
}
